/* Subscribing, on the wire — STORY-032.
 *
 * `pricing.md` owns the numbers and ADR-0007 owns who the seller is; nothing
 * here restates either. What this file fixes is the shape, and the decision in
 * it that matters is what is ABSENT: no card field, no payment method, no
 * amount the client gets to choose. The client names a plan and a period, and
 * every price comes from our catalogue on the server.
 *
 * Trusting a client-supplied price is the textbook `security.md` A01 failure —
 * "never trust a client-supplied id, role, tenant, or price" — and a checkout
 * body is exactly where it is tempting.
 */
import { z } from 'zod';
/** Which of the two prices on a tier — `pricing.md`. */
export const BILLING_PERIODS = ['monthly', 'annual'];
export const startCheckoutRequestSchema = z.object({
    /* Validated against the catalogue server-side. A code that is not a paid tier
     * is refused there rather than trusted here. */
    planCode: z.string().min(1).max(32),
    billingPeriod: z.enum(BILLING_PERIODS),
});
export const startCheckoutResponseSchema = z.object({
    /**
     * Where to send the browser — the provider's domain, never ours.
     *
     * Returned rather than redirected server-side so the client can decide how to
     * navigate, and so the response is testable without following a 303.
     */
    redirectUrl: z.string().url(),
});
/** The four states, in our vocabulary rather than the provider's eight. */
export const SUBSCRIPTION_STATES = [
    'incomplete',
    'active',
    'past_due',
    'unpaid',
    'canceled',
];
export const setCancellationRequestSchema = z.object({
    /** True to stop renewing at period end; false to resume. */
    cancelAtPeriodEnd: z.boolean(),
});
export const subscriptionResponseSchema = z.object({
    /** Null when the workspace has never subscribed — Free is the absence. */
    subscription: z
        .object({
        status: z.enum(SUBSCRIPTION_STATES),
        /** Set to stop renewing. Still `active`, and still entitled, until then. */
        cancelAtPeriodEnd: z.boolean(),
        planCode: z.string(),
        /* Integer minor units plus a currency, never a decimal — ADR-0005. The
         * client formats through `packages/i18n`, which reads the minor-unit
         * digits from the currency rather than assuming two. */
        amountMinor: z.number().int(),
        currency: z.string().length(3),
        /** ISO-8601 with offset. Rendered in the reader's zone — `i18n.md`. */
        currentPeriodEnd: z.string().datetime({ offset: true }).nullable(),
    })
        .nullable(),
});
/* ── the billing trail — STORY-032, STORY-034 ─────────────────────────────── */
/**
 * Who acted, when the actor was one of us.
 *
 * **Absent entirely when the PROVIDER acted**, which is most rows: a webhook has
 * no User behind it. The absence is the fact, and collapsing it into a name like
 * "System" would invent an actor the record does not have.
 */
export const subscriptionActorSchema = z.object({
    /**
     * Null when the account has been erased.
     *
     * The row survives — ADR-0015 keeps the User and anonymises it in place,
     * because a trail with holes is not a trail — but it no longer names
     * anybody, and the client renders its own label rather than an empty string.
     */
    name: z.string().nullable(),
});
export const subscriptionChangeSchema = z.object({
    action: z.enum([
        'transitioned',
        'checkout_started',
        'cancellation_requested',
        'cancellation_lifted',
    ]),
    /** Both null unless this was a transition. */
    fromStatus: z.enum(SUBSCRIPTION_STATES).nullable(),
    toStatus: z.enum(SUBSCRIPTION_STATES).nullable(),
    planCode: z.string().nullable(),
    /** Whether the workspace held a paid plan after this. Null on a request. */
    entitled: z.boolean().nullable(),
    /** ISO-8601 with offset. The provider's clock on a transition, ours on a request. */
    at: z.string(),
    actor: subscriptionActorSchema.nullable(),
});
export const subscriptionHistoryResponseSchema = z.object({
    changes: z.array(subscriptionChangeSchema),
});
//# sourceMappingURL=subscriptions.js.map